import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Key, Plus, Trash2, Copy, Eye, EyeOff, AlertTriangle, RefreshCw, Loader2, Check, Shield, Zap, BarChart2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const PLAN_LIMITS = { essenziale: null, professional: 2000, elite: 10000, lifetime: 10000 };

// Mini bar-chart per uso giornaliero (ultime 30 giorni → mostriamo 14 per spazio)
function UsageChart({ data }) {
  if (!data || data.length === 0) return null;
  const last14 = data.slice(-14);
  const max = Math.max(...last14.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-0.5 h-10 mt-2">
      {last14.map((d, i) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.count} req`}>
          <div className="w-full rounded-sm transition-all"
            style={{
              height: `${Math.max(3, (d.count / max) * 100)}%`,
              background: i === last14.length - 1 ? 'var(--lux-gold)' : 'rgba(201,168,76,0.35)',
              minHeight: '3px',
            }} />
        </div>
      ))}
    </div>
  );
}

function KeyUsagePanel({ keyData, token, tenantSlug, onClose }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}`, 'X-Tenant-Slug': tenantSlug };
    axios.get(`${API}/api/apikeys/${encodeURIComponent(keyData.key_preview)}/usage`, { headers })
      .then(r => setUsage(r.data))
      .catch(() => toast.error('Errore caricamento statistiche'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div className="w-full max-w-md rounded-sm"
        style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'var(--lux-border-1)' }}>
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{keyData.label}</p>
            <code className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>{keyData.key_preview}</code>
          </div>
          <button onClick={onClose} style={{ color: 'var(--lux-text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-5 h-5 border-2 rounded-full animate-spin mx-auto"
                style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
            </div>
          ) : usage ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--lux-gold)' }}>
                  Utilizzo — Ultimi 14 giorni
                </p>
                <span className="text-xs font-medium" style={{ color: 'var(--lux-text)' }}>
                  {usage.total_30d?.toLocaleString('it-IT') || 0} req / 30gg
                </span>
              </div>
              <UsageChart data={usage.daily} />
              <div className="flex justify-between mt-1">
                <span className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>
                  {usage.daily?.[usage.daily.length - 14]?.date || ''}
                </span>
                <span className="text-[10px]" style={{ color: 'var(--lux-gold)' }}>Oggi</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="p-3 rounded-sm text-center"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
                  <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>Totale 30 giorni</p>
                  <p className="text-xl font-serif font-light mt-1" style={{ color: 'var(--lux-text)' }}>
                    {usage.total_30d?.toLocaleString('it-IT') || 0}
                  </p>
                </div>
                <div className="p-3 rounded-sm text-center"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
                  <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>Media giornaliera</p>
                  <p className="text-xl font-serif font-light mt-1" style={{ color: 'var(--lux-text)' }}>
                    {usage.total_30d > 0 ? Math.round(usage.total_30d / 30) : 0}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-center py-6" style={{ color: 'var(--lux-text-muted)' }}>
              Nessun dato disponibile.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function ApiKeys({ token, tenantSlug, tenantPlan }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [revealedKey, setRevealedKey] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [usageKey, setUsageKey] = useState(null); // chiave selezionata per mostrare grafico

  const headers = { Authorization: `Bearer ${token}`, 'X-Tenant-Slug': tenantSlug };

  const canUseApi = ['professional', 'elite', 'lifetime'].includes(tenantPlan);

  useEffect(() => { if (canUseApi) loadKeys(); else setLoading(false); }, []);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/apikeys`, { headers });
      setKeys(data.keys || []);
    } catch {
      toast.error('Errore nel caricamento delle chiavi API.');
    } finally {
      setLoading(false);
    }
  };

  const generateKey = async () => {
    if (!newKeyLabel.trim()) { toast.error('Inserisci un nome per la chiave.'); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/apikeys`, { label: newKeyLabel.trim() }, { headers });
      setRevealedKey(data);
      setNewKeyLabel('');
      await loadKeys();
      toast.success('API Key generata.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella generazione.');
    } finally {
      setGenerating(false);
    }
  };

  const revokeKey = async (keyPreview) => {
    if (!window.confirm('Revocare questa API Key? I sistemi che la usano perderanno immediatamente accesso.')) return;
    setRevoking(keyPreview);
    try {
      await axios.delete(`${API}/api/apikeys/${encodeURIComponent(keyPreview)}`, { headers });
      toast.success('Chiave revocata.');
      await loadKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella revoca.');
    } finally {
      setRevoking(null);
    }
  };

  const copyKey = (val) => {
    navigator.clipboard.writeText(val);
    toast.success('Chiave copiata negli appunti.');
  };

  // ── Upgrade gate ─────────────────────────────────────────────────────────
  if (!canUseApi) {
    return (
      <div data-testid="apikeys-upgrade-gate" className="py-16 text-center">
        <div className="inline-flex w-14 h-14 rounded-full items-center justify-center mb-5"
          style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <Shield size={24} style={{ color: 'var(--lux-gold)' }} />
        </div>
        <h3 className="font-serif text-2xl font-light mb-3" style={{ color: 'var(--lux-text)' }}>
          API Pubblica — Piano Professional o Elite
        </h3>
        <p className="text-sm max-w-md mx-auto mb-6" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.7 }}>
          Le API pubbliche Shangrilux permettono di integrare PMS, sistemi di controllo accessi e channel manager direttamente nella piattaforma.
        </p>
        <div className="flex flex-wrap gap-4 justify-center text-sm" style={{ color: 'var(--lux-text-muted)' }}>
          {[
            'Integrazione PMS (Mews, Opera, Cloudbeds)',
            'Controllo serrature da sistemi esterni',
            'Sincronizzazione prenotazioni channel manager',
          ].map(f => (
            <div key={f} className="flex items-center gap-2">
              <Check size={13} style={{ color: 'var(--lux-gold)' }} /> {f}
            </div>
          ))}
        </div>
        <a href="/pricing" target="_blank"
          className="inline-flex items-center gap-2 mt-8 px-6 py-2.5 text-sm font-medium"
          style={{ background: 'var(--lux-gold)', color: '#000' }}>
          Aggiorna piano <Zap size={14} />
        </a>
      </div>
    );
  }

  return (
    <div data-testid="apikeys-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-2xl font-light" style={{ color: 'var(--lux-text)' }}>API Keys</h2>
          <p className="text-xs mt-1" style={{ color: 'var(--lux-text-muted)' }}>
            Rate limit: {PLAN_LIMITS[tenantPlan]?.toLocaleString('it-IT') || '—'} richieste/ora · Max 5 chiavi attive ·{' '}
            <a href="/developers" target="_blank" style={{ color: 'var(--lux-gold)' }}>Documentazione API →</a>
          </p>
        </div>
        <button onClick={loadKeys} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded"
          style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
          <RefreshCw size={13} /> Aggiorna
        </button>
      </div>

      {/* Revealed new key banner */}
      {revealedKey && (
        <div data-testid="new-key-banner" className="mb-6 p-5 rounded-sm"
          style={{ background: 'rgba(5,150,105,0.07)', border: '1px solid rgba(5,150,105,0.25)' }}>
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} style={{ color: '#059669', marginTop: 2, flexShrink: 0 }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium mb-1" style={{ color: '#059669' }}>
                Chiave generata — salvala subito!
              </p>
              <p className="text-xs mb-3" style={{ color: 'var(--lux-text-muted)' }}>
                Questa è l'unica volta in cui viene mostrata la chiave completa.
              </p>
              <div className="flex items-center gap-2 p-3 rounded font-mono text-xs break-all"
                style={{ background: '#0a0a0a', border: '1px solid var(--lux-border-1)', color: '#e5e5e5' }}>
                <span className="flex-1">{revealedKey.key}</span>
                <button onClick={() => copyKey(revealedKey.key)}
                  className="shrink-0 p-1.5 rounded" style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--lux-gold)' }}>
                  <Copy size={13} />
                </button>
              </div>
            </div>
          </div>
          <button onClick={() => setRevealedKey(null)} className="mt-3 text-xs"
            style={{ color: 'var(--lux-text-muted-3)', background: 'none', border: 'none', cursor: 'pointer' }}>
            Ho salvato la chiave — chiudi
          </button>
        </div>
      )}

      {/* Generate new key */}
      <div className="p-5 rounded-sm mb-6" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
        <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--lux-text-muted)' }}>Genera nuova chiave</p>
        <div className="flex gap-3">
          <input
            data-testid="new-key-label-input"
            type="text"
            placeholder="Nome chiave (es. PMS Mews Production)"
            value={newKeyLabel}
            onChange={e => setNewKeyLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && generateKey()}
            className="flex-1 px-4 py-2.5 text-sm rounded-sm"
            style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}
          />
          <button
            data-testid="generate-key-btn"
            onClick={generateKey}
            disabled={generating || keys.filter(k => k.is_active).length >= 5}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium"
            style={{ background: 'var(--lux-gold)', color: '#000', opacity: generating ? 0.7 : 1 }}>
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Genera
          </button>
        </div>
        {keys.filter(k => k.is_active).length >= 5 && (
          <p className="text-xs mt-2" style={{ color: '#ef4444' }}>
            Limite di 5 chiavi attive raggiunto. Revoca una chiave per generarne una nuova.
          </p>
        )}
      </div>

      {/* Keys list */}
      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--lux-text-muted)' }}>
          <div className="w-5 h-5 border-2 rounded-full animate-spin mx-auto"
            style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
        </div>
      ) : keys.length === 0 ? (
        <div className="text-center py-12" style={{ color: 'var(--lux-text-muted)' }}>
          <Key size={32} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm">Nessuna chiave API. Genera la prima per iniziare ad integrare.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="apikeys-list">
          {keys.map((k, i) => (
            <div key={i} className="flex items-center gap-4 p-4 rounded-sm"
              style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)', opacity: k.is_active ? 1 : 0.5 }}>
              <div className="w-8 h-8 rounded flex items-center justify-center shrink-0"
                style={{ background: k.is_active ? 'rgba(201,168,76,0.1)' : 'rgba(107,114,128,0.1)' }}>
                <Key size={14} style={{ color: k.is_active ? 'var(--lux-gold)' : '#6b7280' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{k.label}</span>
                  {!k.is_active && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: 'rgba(107,114,128,0.15)', color: '#6b7280' }}>Revocata</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-4">
                  <span className="font-mono text-xs" style={{ color: 'var(--lux-text-muted)' }}>{k.key_preview}</span>
                  {k.total_requests > 0 && (
                    <span className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
                      {k.total_requests.toLocaleString('it-IT')} richieste totali
                    </span>
                  )}
                  {k.last_used_at && (
                    <span className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
                      Ultimo uso: {new Date(k.last_used_at).toLocaleDateString('it-IT')}
                    </span>
                  )}
                  <span className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
                    Creata: {new Date(k.created_at).toLocaleDateString('it-IT')}
                  </span>
                </div>
              </div>
              {k.is_active && (
                <>
                  <button
                    data-testid={`revoke-key-${i}`}
                    onClick={() => revokeKey(k.key_preview)}
                    disabled={revoking === k.key_preview}
                    className="p-2 rounded shrink-0 transition-colors"
                    style={{ color: '#ef4444', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.15)' }}
                    title="Revoca chiave">
                    {revoking === k.key_preview
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Trash2 size={14} />}
                  </button>
                  <button
                    data-testid={`usage-key-${i}`}
                    onClick={() => setUsageKey(k)}
                    className="p-2 rounded shrink-0 transition-colors"
                    style={{ color: 'var(--lux-gold)', background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.15)' }}
                    title="Statistiche utilizzo">
                    <BarChart2 size={14} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {usageKey && (
        <KeyUsagePanel
          keyData={usageKey}
          token={token}
          tenantSlug={tenantSlug}
          onClose={() => setUsageKey(null)}
        />
      )}

      {/* Quick start */}
      <div className="mt-8 p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
        <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--lux-gold)' }}>Quick start</p>
        <pre className="text-xs leading-relaxed overflow-x-auto p-3 rounded"
          style={{ background: '#0a0a0a', color: '#a8b5c8', fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>
{`# Testa la connessione con la tua API Key
curl -H "X-API-Key: ostx_..." \\
     ${API}/api/v1/

# Lista prenotazioni
curl -H "X-API-Key: ostx_..." \\
     ${API}/api/v1/bookings?status=confirmed

# Crea prenotazione da PMS
curl -X POST ${API}/api/v1/bookings \\
     -H "X-API-Key: ostx_..." \\
     -H "Content-Type: application/json" \\
     -d '{"property_id":"...", "guest_name":"Mario Rossi", "guest_email":"mario@email.it", "checkin_at":"2026-06-01", "checkout_at":"2026-06-05"}'`}
        </pre>
        <a href="/developers" target="_blank"
          className="inline-flex items-center gap-1.5 mt-3 text-xs"
          style={{ color: 'var(--lux-gold)' }}>
          Documentazione completa →
        </a>
      </div>
    </div>
  );
}
